// Response shapes of the Bee API (see server/routes/*). Keep in sync with the server.

export type ChangeState =
  "created" | "ci" | "review" | "ready" | "train" | "landing" | "landed" | "verified" | "reverted" | "rejected" | "conflict" | "cancelled";

/** States of a change that is still on its way to main (what the "Open" filter shows). */
export const OPEN_STATES: ChangeState[] = ["created", "ci", "review", "ready", "train", "landing", "conflict"];

/** States the server is actively working on; pages poll faster and show live indicators. */
export const ACTIVE_STATES: ChangeState[] = ["created", "ci", "ready", "train", "landing"];

export const openCount = (counts: Partial<Record<string, number>>) => OPEN_STATES.reduce((n, s) => n + (counts[s] ?? 0), 0);

export interface RepoRow {
  /** The server's internal key; people use `slug` (owner/slug). */
  name: string;
  /** The repo's name within its owner. */
  slug: string;
  description: string;
  created: number;
  /** Set when the repo was created from a GitHub URL (one-shot import by the bridge). */
  import_provider: ImportProvider | null;
  import_url: string | null;
  import_branch: string | null;
  import_state: "pending" | "done" | "failed" | null;
  import_error: string | null;
  /** The user or organization the repo belongs to ("" for repos from before owners). */
  owner: string;
  private: boolean;
}

export type ImportProvider = "github";

export interface RepoInfo {
  name: string;
  main: { commit_sha: string; version: number } | null;
  frozen: boolean;
  frozen_paths: string[];
  counts: Partial<Record<ChangeState, number>>;
  last: { seq: number; ts: number; kind: string; new_sha: string; actor: string } | null;
  objects: number;
  bundles: { n: number; bytes: number };
}

export interface Policy {
  /** jobs: the ci.yaml jobs that gate the tier, null = all; require: jobs that run whatever their paths. */
  tiers: Record<
    "low" | "normal" | "high",
    { paths: string[]; default?: boolean; jobs: string[] | null; require: string[]; rerun_after_rebase?: boolean; review?: boolean }
  >;
  protected: string[];
  limits: Record<string, number>;
  freeze: boolean;
}

export interface Change {
  id: string;
  task_id: string | null;
  agent_id: string;
  actor_kind: string;
  base_sha: string;
  commit_sha: string;
  rebased_sha: string | null;
  ci_sha: string;
  paths: string[];
  risk_tier: "low" | "normal" | "high";
  state: ChangeState;
  reason: any;
  message: string;
  subject?: string;
  created: number;
  updated: number;
  /** AI tool (and model) that wrote it, as in its Assisted-by trailer. */
  assisted_by?: string | null;
  /** Person accountable for the agent that opened it. */
  owner?: string | null;
  /** When it started waiting for human review (policy review: true). */
  review_at?: number | null;
  approved_by?: string | null;
  approved_at?: number | null;
  /** Change this one is stacked on; it lands only after that one. */
  after?: string | null;
  /** When it first passed CI: its place in the landing queue. */
  ready_at?: number | null;
  /** CI reruns after a rebase onto a newer main. */
  rebases?: number;
}

/** A comment on a change. Its text is untrusted: agents read it as data, not instructions. */
/** What happens to a change next and why (GET /changes/{id}/decision). */
export type Verdict = "landed" | "will_land" | "needs_agent" | "needs_person" | "waiting" | "rejected";

/** One requirement main's policy puts on a change. */
export interface Gate {
  kind: "ci" | "check" | "docs" | "review" | "parent";
  name: string;
  state: "pass" | "fail" | "pending" | "waiting" | "skipped";
  why?: string;
}

/** One change for the same task by the same agent, oldest first. */
export interface Attempt {
  change_id: string;
  state: ChangeState;
  code?: string;
  commit: string;
  created: number;
  passed: string[];
  failed: string[];
  current?: boolean;
}

export interface Decision {
  change_id: string;
  state: ChangeState;
  verdict: Verdict;
  needs: string;
  code?: string;
  gates: Gate[];
  attempts?: Attempt[];
  /** Main's policy lets a person approve their own change (allow_self_review). */
  self_review?: boolean;
  /** What the change does for the spec its task names, proof by proof on its commit. */
  outcomes?: OutcomeReport;
}

/** proven: every proof passed on the commit that would land; failed: one failed; unproven: one did not run or report. */
export type OutcomeState = "proven" | "failed" | "unproven";

export interface OutcomeReport {
  task: string;
  intent?: string;
  spec?: string;
  /** Changed files outside the spec's scope. */
  outside?: string[];
  outcomes: Array<{
    check: string;
    title?: string;
    on_main: CheckState;
    state: OutcomeState;
    proofs: Array<{
      proof: Proof;
      result: "pass" | "fail" | "missing" | "not_reported" | "running" | "not_run";
      /** The job run that decided it. */
      job?: string;
      /** The change wrote this test for itself: no person approved it before the code. */
      self_authored?: boolean;
    }>;
  }>;
  proven: number;
  failed: number;
  unproven: number;
  self_authored: number;
  /** The tier asks a person to decide from this report (review: outcome). */
  review?: boolean;
}

export interface ChangeComment {
  id: string;
  actor: string;
  app?: string;
  body: string;
  ts: number;
}

/** Other agents' work on the same files, from POST /plan. */
export interface Overlap {
  change_id: string;
  agent: string;
  task: string | null;
  state: ChangeState;
  message: string;
  paths: string[];
  same_task?: boolean;
}

export interface Evidence {
  change_id: string;
  job: string;
  attempt: number;
  commit_sha: string;
  stage: number;
  /** waiting: dispatched, but held until every job in `needs` passes. */
  status: "waiting" | "queued" | "pass" | "fail" | "infra_error" | "skipped";
  ms: number | null;
  log_key: string | null;
  summary: string | null;
  queued_at: number;
  /** Concrete jobs of the same run that must pass first. */
  needs: string[] | null;
  /** The ci.yaml job a matrix instance was expanded from. */
  job_group: string | null;
}

/** One run: every job of a change (or verification probe) on one commit, latest attempt each. */
export interface CiRun {
  subject: string;
  commit: string;
  jobs: Evidence[];
  /** Attempts per job name. */
  attempts: Record<string, number>;
  change: { agent_id: string; state: ChangeState; subject: string } | null;
}

export interface CiJobConfig {
  name: string;
  instance: string;
  image?: string;
  cmd: string;
  timeout: number;
  paths?: string[];
  needs?: string[];
  matrix?: Array<{ name: string; values: string[] }>;
}

/** A job attempt's uploaded artifacts (one tar.gz). */
export interface CiArtifact {
  size: number;
  files: number;
  created: number;
  expires: number;
}

export interface AuditEntry {
  seq: number;
  ts: number;
  actor: string;
  action: string;
  detail: any;
}

export interface LogEntry {
  seq: number;
  ts: number;
  kind: string;
  old_sha: string | null;
  new_sha: string;
  change_id: string | null;
  actor: string;
  agent: string | null;
  task: string | null;
  subject: string | null;
  risk_tier: string | null;
  change_state: string | null;
  paths: string[] | null;
  evidence: Record<string, number>;
}

export interface TreeEntry {
  path: string;
  name: string;
  type: "tree" | "blob" | "link" | "commit";
  mode: string;
  sha: string;
}

export interface FileView {
  commit: string;
  path: string;
  sha: string;
  mode: string;
  size: number;
  binary: boolean;
  content: string | null;
}

export interface DiffFile {
  path: string;
  status: "added" | "deleted" | "modified" | "mode";
  binary: boolean;
  additions: number;
  deletions: number;
  patch?: string;
}

export interface CommitView {
  sha: string;
  tree: string;
  parents: string[];
  author: string;
  committer: string;
  message: string;
  trailers: Record<string, string>;
  change_id: string | null;
  files?: DiffFile[];
}

export interface Verification {
  id: number;
  from_seq: number;
  to_seq: number;
  head_sha: string;
  state: string;
  phase: string;
  probe: number;
  result: string | null;
  created: number;
  updated: number;
}

export interface CiOverview {
  recent: Evidence[];
  /** The changes behind `recent`, keyed by change id (verification runs have none). */
  changes: Record<string, { subject: string; agent_id: string; state: ChangeState }>;
  flaky: Array<{ job: string; flagged: number; quarantined: number; note: string | null }>;
  verifications: Verification[];
  totals: Array<{ job: string; runs: number; pass: number; fail: number; ms: number }>;
  last_verified_seq: number;
  config: { jobs: CiJobConfig[] } | null;
}

/** GET /v1/repos/:repo/stats: main's health now, plus activity over the last `hours`. */
export interface RepoStats {
  hours: number;
  since: number;
  main: {
    /** verified: every land passed post-land verification; pending: lands wait for the next window; failing: inconclusive, paths frozen. */
    status: "empty" | "verified" | "verifying" | "pending" | "failing";
    last_verified_seq: number;
    last_verified_at: number | null;
    /** Lands and reverts after last_verified_seq. */
    unverified: number;
    quarantined: number;
    verification: Pick<Verification, "id" | "state" | "phase" | "head_sha" | "result" | "updated"> | null;
  };
  throughput: {
    landed: number;
    reverted: number;
    rejected: number;
    cancelled: number;
    conflicts: number;
    land_ms_p50: number | null;
    land_ms_p90: number | null;
  };
  /** Job results queued in the window; `pending` counts every waiting or queued job. */
  ci: { pass: number; fail: number; infra_error: number; job_ms_p50: number | null; pending: number };
  /** Work spent to land: each `bee land` registers a change (an attempt); discarded = rejected, cancelled or reverted. */
  cost: { attempts: number; discarded: number; attempts_per_land: number | null; ci_ms: number; discarded_ci_ms: number };
  /** How well many agents landing at once works; rates are null when there is nothing to divide by. */
  health: {
    review: { waiting: number; oldest_at: number | null; approved: number; rejected: number; pickup_ms_p50: number | null; pickup_ms_p90: number | null };
    train_ejected: number;
    train_fail_rate: number | null;
    revert_rate: number | null;
    /** Land attempts that hit a write conflict. */
    conflicts: number;
    conflict_rate: number | null;
    /** agent's changes conflicted with lines with_agent landed first. */
    conflict_pairs: Array<{ agent: string; with_agent: string; count: number }>;
    parent_failed: number;
  };
  /** Agents with open changes or activity in the window, most open changes first. */
  agents: Array<{ agent_id: string; open: number; attempts: number; landed: number; rejected: number; reverted: number; last_active: number; tasks: string[] }>;
  /** Equal-width buckets covering the window, oldest first. */
  series: Array<{ ts: number; landed: number; reverted: number }>;
}

export interface QueueStats {
  jobs: Array<{ kind: string; state: string; n: number }>;
  claimed: Array<{ id: number; kind: string; repo: string; claimed_by: string; claimed_at: number; payload: string }>;
  /** Oldest first, at most 100; `jobs` has the full counts. */
  queued: Array<{ id: number; kind: string; repo: string; created: number; payload: string }>;
  /** Workers the server starts itself when work is queued (global queue only), so none running is normal. */
  on_demand?: string[];
}

/** Signed token contents, as returned by POST /v1/admin/tokens and recorded in token.issue audit entries. */
export interface TokenClaims {
  sub: string;
  kind: Actor["kind"];
  task?: string | null;
  repos: string[];
  paths: string[];
  actions: string[];
  exp: number;
  jti: string;
}

export interface Actor {
  sub: string;
  kind: "agent" | "admin" | "runner" | "bridge";
  actions: string[];
  task?: string;
  /** Set for people signed in with GitHub (a session, not a token). */
  user?: string;
  /** Repo name → extra actions there, from repository access granted to the user or their groups. */
  grants?: Record<string, string[]>;
}

/** Whether actor may perform action in repo, with its own actions or a grant on the repo (auth.Claims.CanIn). */
export const canIn = (actor: Actor | null | undefined, action: string, repo: string) =>
  !!actor && (actor.actions.includes(action) || !!actor.grants?.[repo]?.includes(action));

/** A sign-in provider the server has configured (GET /auth/providers). */
export interface AuthProvider {
  id: ImportProvider;
  name: string;
  host: string;
}

/** A user's site role; "none" reaches only the repos granted to them. */
export type UserRole = "none" | "read" | "write" | "admin";
/** A role granted on one repository. */
export type GrantRole = Exclude<UserRole, "none">;

/** A person who signed in with GitHub. */
export interface User {
  id: string;
  name: string;
  display: string;
  email: string;
  avatar_url: string;
  role: UserRole;
  disabled: boolean;
  created: number;
  last_login: number;
}

/** A user's or group's role on one repository (GET /v1/repos/{repo}/access). */
export interface RepoGrant {
  kind: "users" | "groups";
  id: string;
  name: string;
  /** Display name of a user, description of a group. */
  display: string;
  avatar_url: string;
  role: GrantRole;
  granted: number;
  granted_by: string;
}

/** A provider account whose token imports and syncs use; the token is never returned. */
export interface Connection {
  id: string;
  provider: ImportProvider;
  host: string;
  login: string;
  token_expires: number;
  scopes: string;
  /** sign_in_only: never granted repository access, so no token; revoked: the provider refused the token; disconnected: its user erased it. */
  status: "ok" | "sign_in_only" | "revoked" | "disconnected";
  created: number;
  updated: number;
}

/** An organization the signed-in user belongs to, with their role in it (GET /v1/me). */
export interface UserOrg {
  id: string;
  name: string;
  description: string;
  role: "owner" | "member";
}

/** An organization with its members (GET /v1/orgs/{org}). */
export interface OrgDetail {
  id: string;
  name: string;
  description: string;
  created: number;
  members: { id: string; name: string; display: string; avatar_url: string; role: "owner" | "member" }[];
}

/** GET /v1/me */
export interface Me {
  actor: Actor;
  user: User | null;
  connections: Connection[];
  orgs: UserOrg[];
  providers: AuthProvider[];
}

/** A repository a connection can import (GET /v1/me/connections/{id}/repos). */
export interface ProviderRepo {
  path: string;
  url: string;
  default_branch: string;
  description: string;
  private: boolean;
  push: boolean;
}

/** The connection whose token the bridge uses for a repo (GET /v1/repos/{repo}/connection). */
export interface RepoConnection {
  repo: string;
  provider: ImportProvider;
  host: string;
  login: string;
  status: Connection["status"];
  bound_by: string;
  created: number;
}

export interface ApiError {
  status: "error";
  code: string;
  message: string;
  hint: string;
  /** Structured context; validation failures carry `{ issues: ["field: message", ...] }`. */
  detail?: unknown;
}

/** A repo's remote in mode pr: landed work ships as batch PRs/MRs, and the remote branch is pulled into main. */
export interface Remote {
  repo: string;
  url: string;
  provider: ImportProvider;
  branch: string;
  batch_size: number;
  batch_wait_ms: number;
  auto_merge: boolean;
  merge_method: "merge" | "squash" | "rebase";
  pulled_sha: string | null;
  shipped_seq: number;
  checked_at: number | null;
  error: string | null;
  error_code: "conflict" | "bridge" | null;
  created: number;
  updated: number;
}

export type BatchState = "pending" | "open" | "merged" | "closed";

export interface Batch {
  repo: string;
  n: number;
  state: BatchState;
  head_sha: string;
  base_sha: string;
  from_seq: number;
  to_seq: number;
  lands: number;
  branch: string;
  title: string;
  body: string;
  pr_number: number | null;
  pr_url: string | null;
  merge_sha: string | null;
  created: number;
  updated: number;
}

export interface RemoteLand {
  seq: number;
  ts: number;
  kind: string;
  sha: string;
  change_id: string | null;
  agent: string | null;
  task: string | null;
  subject: string;
}

export interface RemoteStatus {
  remote: Remote | null;
  active: Batch | null;
  batches: Batch[];
  pending: { lands: number; oldest: number | null; last_seq: number | null };
  next: RemoteLand[];
  next_cut: number | null;
  blocked: string;
}

/** Where CI runners run; see internal/server/modules/runners. */
export type RunnerPlatform = "kind" | "gcp" | "aws" | "cloudflare" | "ubicloud";

/** Where a placement can deploy runners; "aws" is only what older runners report. */
export type PlacementPlatform = "kind" | "gcp" | "cloudflare";

/** How CI runners are deployed on one platform; there is at most one per platform. */
export interface RunnerPlacement {
  platform: PlacementPlatform;
  /** The platform's own runner pool: on for every organization and read-only to them. */
  managed?: boolean;
  server_url: string;
  image: string;
  replicas: number;
  concurrency: number;
  cpu?: string;
  memory?: string;
  spot: boolean;
  namespace?: string;
  cluster?: string;
  project?: string;
  region?: string;
  account?: string;
  instance_type?: string;
}

export interface StoredPlacement {
  placement: RunnerPlacement;
  /** Runners of the managed pool that polled in the last minute; only on a managed placement. */
  online?: number;
  setup: RunnerSetup;
  updated: number;
  updated_by: string;
}

export interface RunnerSetup {
  subject: string;
  steps: Array<{ title: string; detail?: string; command?: string }>;
  files: Array<{ path: string; content: string }>;
}

/** A runner process (instance) as last seen polling the queue. */
export interface RunnerSeen {
  /** "<token subject>/<instance>". */
  name: string;
  /** "" when the runner does not report one (started without --platform). */
  platform: RunnerPlatform | "";
  /** The registered runner whose token it uses; "" = a signed runner token. */
  runner_id: string;
  /** Reported by the runner; "" for runners older than this field. */
  version: string;
  os: string;
  arch: string;
  /** As the server saw it. */
  ip: string;
  /** Parallel jobs it runs; 0 = not reported. */
  concurrency: number;
  /** Labels it offers jobs' runs_on (os:, arch:, docker, --label, platform:), sorted; [] for runners older than labels. */
  labels: string[];
  first_seen: number;
  last_seen: number;
  busy: number;
}

/** A runner created by an admin, with its own revocable token (POST /v1/admin/runners). The token is never returned again. */
export interface RegisteredRunner {
  id: string;
  name: string;
  description: string;
  /** "" = any; otherwise only instances reporting it get CI jobs. */
  platform: RunnerPlatform | "";
  /** Repositories it runs CI for; empty = all. */
  repos: string[];
  paused: boolean;
  /** Last 4 characters of the token. */
  token_hint: string;
  /** Unix ms; 0 = never. */
  token_expires: number;
  token_rotated: number;
  /** Last poll by any instance; 0 = never contacted. */
  contacted: number;
  created: number;
  created_by: string;
}

/** What ran on the platform's managed runners in a month (UTC): the organization's own row, or every organization's for the site. */
export interface RunnerUsage {
  /** YYYY-MM */
  month: string;
  rows: Array<{ owner: string; jobs: number; seconds: number; failed: number }>;
  /** The organization's monthly cap in runner minutes (0 = none), and whether it is used up. Organization view only. */
  limit_minutes: number;
  capped: boolean;
  /** The site's caps: the default and per-organization overrides (0 lifts the cap). Site view only. */
  limits?: { default_minutes: number; orgs: Record<string, number> };
}

export interface RunnersView {
  usage: RunnerUsage;
  registered: RegisteredRunner[];
  /** In platform order. */
  placements: StoredPlacement[];
  /** Only runners reporting a placed platform get CI jobs. */
  strict: boolean;
  runners: RunnerSeen[];
  platforms: RunnerPlatform[];
  instance_types: string[];
}

/** A file in the code graph (GET /v1/repos/:repo/graph). */
export interface CodeGraphNode {
  path: string;
  lang: string;
  rank: number;
  symbols: number;
  focus?: boolean;
}

/** `from` uses identifiers that `to` defines. */
export interface CodeGraphEdge {
  from: string;
  to: string;
  weight: number;
  names: string[];
}

export interface CodeGraph {
  commit: string;
  nodes: CodeGraphNode[];
  edges: CodeGraphEdge[];
  /** Files that matched before the node limit. */
  total: number;
  truncated: boolean;
  index_truncated: boolean;
}

/** A Markdown file the repo's knowledge config includes, as a node; its id is its repo path. */
export interface KnowledgeDoc {
  id: string;
  title: string;
  /** adr, rfc, plan, doc, or a type from .bee/knowledge.yaml. */
  type: string;
  status?: string;
  /** The status's class, whatever the team calls the status. */
  class?: DocClass;
  date?: string;
  owner?: string;
  tags: string[];
  /** Files or directories the document is about. */
  paths: string[];
  summary: string;
}

export interface KnowledgeEdge {
  src: string;
  dst: string;
  relation: string;
}

/** A document type and its lifecycle, from .bee/knowledge.yaml or the defaults. */
export interface KnowledgeType {
  name: string;
  label: string;
  paths: string[];
  statuses: string[];
  /** Every status of the type mapped to its class. */
  classes?: Record<string, DocClass>;
  dir: string;
  numbered: boolean;
  template?: string;
  /** Set when the type's documents name checks (spec requirements, plan "done when" items). */
  checks?: CheckRule;
  count: number;
}

export interface Knowledge {
  commit: string;
  scopes: string[];
  /** .bee/knowledge.yaml exists on main. */
  configured: boolean;
  config_error?: string;
  types: KnowledgeType[];
  docs: KnowledgeDoc[];
  edges: KnowledgeEdge[];
  truncated: boolean;
}

/** Every status belongs to one class (OKF v0.2): draft (being written, checks not enforced),
 * stable (in force, checks enforced) or deprecated (kept for history). */
export type DocClass = "draft" | "stable" | "deprecated";

/** How a type's documents name checks: a heading per check and a key line naming its proof. */
export interface CheckRule {
  heading: string;
  key: string;
  score?: string;
  lint?: string[];
}

/** One test that proves a check: `<job>: <file>::<test>`, or `<job>: <test>`. */
/** What a proof shows, from the code up to what a person sees; absent is a plain test. */
export type ProofKind = "test" | "spec-test" | "e2e" | "journey" | "smoke";

export interface Proof {
  kind?: ProofKind;
  job: string;
  file?: string;
  test: string;
}

/** holds: every proof passes on main; broken: one fails; pending: not run yet or the test is missing. */
export type CheckState = "holds" | "broken" | "pending";

/** A requirement in a stable document, with its state derived from CI on main. */
export interface DocCheck {
  /** <file slug>/<id> */
  id: string;
  /** The document's repo path. */
  doc: string;
  title: string;
  /** Line of the check's heading in the document, 1-based. */
  line: number;
  proofs: Proof[];
  /** The measurable bar, `<metric> <op> <number>`; proofs' jobs print `BEE_METRIC <metric>=<value>`. */
  score?: string;
  /** The section under the heading. */
  text: string;
  state: CheckState;
  /** Why the check does not hold, when it does not. */
  reason?: string;
  /** The score metric as measured on main, when the check has a score. */
  metric?: number;
  evidence: CheckEvidence[];
}

/** The latest CI result behind one proof. */
export interface CheckEvidence {
  proof: Proof | string;
  state: CheckState;
  commit?: string;
  run_id?: string;
  /** What the run printed as BEE_METRIC name=value. */
  metrics?: Record<string, number>;
}

/** A document that does not follow its type's rules, at a line. */
export interface DocProblem {
  path: string;
  line: number;
  message: string;
}

/** What needs a person: checks that are broken or pending, documents in draft, plans in force, rule problems. */
export interface Attention {
  commit: string;
  /** Broken first, then pending. */
  checks: DocCheck[];
  /** How many checks hold. */
  holding: number;
  drafts: KnowledgeDoc[];
  active_plans: KnowledgeDoc[];
  problems: DocProblem[];
}

export interface KnowledgeHit extends KnowledgeDoc {
  snippet: string;
  score: number;
}

/** open: no stable spec translates it yet; in_progress: its spec is stable, not every check holds;
 *  proven: every check of its spec holds on main; regressed: it was proven and a check stopped holding. */
export type IntentState = "open" | "in_progress" | "proven" | "regressed";

/** What a person asked for, in their own words (bee intent new, /intent). */
export interface Intent {
  id: string;
  title: string;
  /** The person's words, verbatim. */
  words: string;
  /** The person accountable. */
  owner: string;
  /** Who recorded it: the person, or the agent acting for them. */
  author: string;
  state: IntentState;
  proven_at: number | null;
  created: number;
  updated: number;
  /** On lists: who acts next (person: approve the spec or review a change; agent: write, build, fix). */
  waiting_on?: "person" | "agent";
}

/** An intent with its spec, each outcome (a check of the spec) and the changes whose task it is. */
export interface IntentView extends Intent {
  spec: { doc: string; title: string; status: string; class: DocClass; commit: string; checks: DocCheck[] } | null;
  outcomes: DocCheck[];
  changes: Array<{
    id: string;
    state: ChangeState;
    message: string;
    agent: string;
    risk_tier: string;
    created: number;
    updated: number;
    paths?: string[];
    /** While in review: the docs with checks (the spec) a person approves. */
    docs?: string[];
  }>;
  next?: string;
}
